import { Component, OnDestroy, OnInit } from "@angular/core";
import { ComponentStore, tapResponse } from "@ngrx/component-store";
import { combineLatest, Subject } from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";
import { SpecialPurposeAssetClass } from "src/app/core/v3/comparable/special-purpose";
import { SpecialPurposePropertyTypeService } from "src/app/core/v3/comparable/special-purpose/property-types";
import { SpecialPurposeAssetClassAPIService } from "src/app/core/v3/comparable/special-purpose/special-purpose-service";
import { tenureAdder } from 'src/app/core/comparable/_data-sources/tenure_extension'
import { User } from "src/app/core/auth";
import { Store } from "@ngrx/store";
import { currentUser } from "src/app/core/mad-auth/mad-auth.store";

interface State {
  items: SpecialPurposeAssetClass[]
}

@Component({
  selector: 'app-special-purpose-comparable-list',
  templateUrl: './special-purpose-comparable-list.component.html',
  providers: [ComponentStore]
})
export class SpecialPurposeComparableListComponent implements OnInit, OnDestroy {
  // State
  private _items$ = this._store.select(state => state.items)
  private _types$ = this._propertyTypeService.types$
  currentUser: User | null

  // Table
  displayedColumns = ['id', 'property_type', 'size', 'consideration', 'source_date', 'actions']
  dataSource$ = combineLatest([
    this._items$,
    this._types$
  ]).pipe(
    map(([items, types]) => {
      return items.map(item => {
        const type = types.find(t => t.id === item.property_type_id)
        const withDisplayTenures = tenureAdder(item)
        return {
          id: item.id,
          status: item.status,
          ref_num: item.ref_num,
          locationData: {
            time_offset: null
          },
          property_type_name: type ? type.name : 'N/A',
          size: item.size,
          display_tenures: withDisplayTenures.display_tenures,
          used_times: item.used_times,
          can_be_deleted: item.can_be_deleted,
          createdBy: item.createdBy,
          created_at: item.created_at,
          lastUpdatedBy: item.lastUpdatedBy,
          updated_at: item.updated_at
        }
      })
    })
  )

  private _onDestroy$ = new Subject<void>()

  constructor(
    private _ngStore: Store,
    private _store: ComponentStore<State>,
    private _apiService: SpecialPurposeAssetClassAPIService,
    private _propertyTypeService: SpecialPurposePropertyTypeService
  ) {
    this._store.setState({
      items: []
    })

    this._fetchList()
  }

  ngOnInit(): void {
    this._ngStore.select(currentUser).pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(user => this.currentUser = user)
  }

  ngOnDestroy(): void {
    this._onDestroy$.next()
    this._onDestroy$.complete()
  }

  onDelete = this._store.effect<number>(id$ =>
    id$.pipe()
  )

  getItemStatusString(status: number): string {
    switch (status) {
      case 0:
        return 'Draft';
      case 2:
        return 'imported';
      default:
        return 'Validated';
    }
  }

  private _fetchList = this._store.effect<void>(trigger$ => 
    trigger$.pipe(
      switchMap(() => 
        this._apiService.fetchList().pipe(
          tapResponse(
            (response) => {
              this._store.patchState({
                items: response.data
              })
            },
            (err) => {
              console.error(err)
            }
          )
        )
      )
    )
  )
}